import React, { useState } from "react";

function ChildrenHeightPredictor() {
  const [formData, setFormData] = useState({
    gender: "female",
    ageYears: "",
    ageMonths: "",
    cFeet: "",
    cInch: "0",
    cWeight: "",
    weightUnit: "pounds",
    mFeet: "",
    mInch: "0",
    fFeet: "",
    fInch: "0",
    result: ""
  });
  
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Function to validate if input is a number and prevent negative values
  function gIsNumber(strNumber, strMin, strMax) {
    if (!strNumber || strNumber.length === 0) return false;
    
    // Convert to string and check each character
    const strTempNumber = String(strNumber);
    for (let i = 0; i < strTempNumber.length; i++) {
      const char = strTempNumber.charAt(i);
      if (!'0123456789'.includes(char)) {
        return false;
      }
    }

    const numValue = parseFloat(strTempNumber);
    if (isNaN(numValue)) return false;

    // Prevent negative values
    if (numValue < 0) return false;

    // Check min/max bounds if provided
    if (strMin !== null && strMin !== undefined && numValue < parseFloat(strMin)) return false;
    if (strMax !== null && strMax !== undefined && numValue > parseFloat(strMax)) return false;

    return true;
  }

  // Real-time validation for height fields
  const validateHeightField = (name, value, type) => {
    const newErrors = { ...errors };
    
    if (value === '') {
      newErrors[name] = "This field is required";
    } else if (!gIsNumber(value)) {
      newErrors[name] = "Please enter a valid number";
    } else {
      const numValue = parseInt(value);
      if (name.includes('Feet')) {
        if (numValue < 1 || numValue > 9) {
          newErrors[name] = "Feet should be between 1 and 9";
        } else {
          delete newErrors[name];
        }
      } else if (name.includes('Inch')) {
        if (numValue < 0 || numValue > 11) {
          newErrors[name] = "Inches should be between 0 and 11";
        } else {
          delete newErrors[name];
        }
      }
    }
    
    setErrors(newErrors);
  };

  // Real-time validation for weight field
  const validateWeightField = (value, unit) => {
    const newErrors = { ...errors };
    
    if (value === '') {
      newErrors.cWeight = "Weight is required";
    } else if (!gIsNumber(value)) {
      newErrors.cWeight = "Please enter a valid weight";
    } else {
      const weightNum = parseFloat(value);
      if (unit === "pounds") {
        if (weightNum < 4 || weightNum > 1550) {
          newErrors.cWeight = "Weight should be between 4 and 1550 pounds";
        } else {
          delete newErrors.cWeight;
        }
      } else {
        if (weightNum < 2 || weightNum > 700) {
          newErrors.cWeight = "Weight should be between 2 and 700 kgs";
        } else {
          delete newErrors.cWeight;
        }
      }
    }
    
    setErrors(newErrors);
  };

  // Real-time validation for age fields
  const validateAgeField = (name, value) => {
    const newErrors = { ...errors };
    
    if (value === '') {
      newErrors[name] = name === 'ageYears' ? "Years is required" : "Months is required";
    } else if (!gIsNumber(value)) {
      newErrors[name] = "Please enter a valid number";
    } else {
      const numValue = parseInt(value);
      if (name === 'ageYears') {
        if (numValue < 4 || numValue > 17) {
          newErrors[name] = "Age should be between 4 and 17 years";
        } else {
          delete newErrors[name];
        }
      } else if (name === 'ageMonths') {
        if (numValue < 0 || numValue > 11) {
          newErrors[name] = "Months should be between 0 and 11";
        } else {
          delete newErrors[name];
        }
      }
    }
    
    setErrors(newErrors);
  };

  // Handle input change with real-time validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent negative values and non-numeric input
    if (value !== '' && (value < 0 || !/^\d*$/.test(value))) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation based on field type
    if (name.includes('Feet') || name.includes('Inch')) {
      validateHeightField(name, value, name.charAt(0));
    } else if (name === 'cWeight') {
      validateWeightField(value, formData.weightUnit);
    } else if (name === 'ageYears' || name === 'ageMonths') {
      validateAgeField(name, value);
    }
  };

  // Handle blur event to mark field as touched
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  // Convert feet and inches to total inches
  function gToInches(feet, inches) {
    if (!gIsNumber(feet) || !gIsNumber(inches)) return false;
    return parseInt(feet) * 12 + parseInt(inches);
  }

  // Convert total inches to feet and inches format
  function gToFeetAndInches(totalInches) {
    if (!gIsNumber(totalInches)) return false;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet} Feet ${inches} Inches`;
  }

  // Validate all form fields before calculation
  function validateAllFields() {
    const newErrors = {};
    const { ageYears, ageMonths, gender, mFeet, mInch, fFeet, fInch, cFeet, cInch, cWeight, weightUnit } = formData;

    // Validate gender
    if (!gender) {
      newErrors.gender = "Please select your child's gender";
    }

    // Validate age
    if (!ageYears) {
      newErrors.ageYears = "Years is required";
    } else if (!gIsNumber(ageYears)) {
      newErrors.ageYears = "Please enter a valid number for years";
    } else if (parseInt(ageYears) < 4 || parseInt(ageYears) > 17) {
      newErrors.ageYears = "Age should be between 4 and 17 years";
    }

    if (!ageMonths) {
      newErrors.ageMonths = "Months is required";
    } else if (!gIsNumber(ageMonths)) {
      newErrors.ageMonths = "Please enter a valid number for months";
    } else if (parseInt(ageMonths) < 0 || parseInt(ageMonths) > 11) {
      newErrors.ageMonths = "Months should be between 0 and 11";
    }

    // Validate mother's height
    if (!mFeet) {
      newErrors.mFeet = "Mother's feet is required";
    } else if (!gIsNumber(mFeet)) {
      newErrors.mFeet = "Please enter a valid number for feet";
    } else if (parseInt(mFeet) < 1 || parseInt(mFeet) > 9) {
      newErrors.mFeet = "Feet should be between 1 and 9";
    }

    // Mother's inches is now mandatory with default value
    if (!mInch && mInch !== "0") {
      newErrors.mInch = "Mother's inches is required";
    } else if (!gIsNumber(mInch)) {
      newErrors.mInch = "Please enter a valid number for inches";
    } else if (parseInt(mInch) < 0 || parseInt(mInch) > 11) {
      newErrors.mInch = "Inches should be between 0 and 11";
    }

    // Validate father's height
    if (!fFeet) {
      newErrors.fFeet = "Father's feet is required";
    } else if (!gIsNumber(fFeet)) {
      newErrors.fFeet = "Please enter a valid number for feet";
    } else if (parseInt(fFeet) < 1 || parseInt(fFeet) > 9) {
      newErrors.fFeet = "Feet should be between 1 and 9";
    }

    // Father's inches is now mandatory with default value
    if (!fInch && fInch !== "0") {
      newErrors.fInch = "Father's inches is required";
    } else if (!gIsNumber(fInch)) {
      newErrors.fInch = "Please enter a valid number for inches";
    } else if (parseInt(fInch) < 0 || parseInt(fInch) > 11) {
      newErrors.fInch = "Inches should be between 0 and 11";
    }

    // Validate child's height
    if (!cFeet) {
      newErrors.cFeet = "Child's feet is required";
    } else if (!gIsNumber(cFeet)) {
      newErrors.cFeet = "Please enter a valid number for feet";
    } else if (parseInt(cFeet) < 1 || parseInt(cFeet) > 9) {
      newErrors.cFeet = "Feet should be between 1 and 9";
    }

    // Child's inches is now mandatory with default value
    if (!cInch && cInch !== "0") {
      newErrors.cInch = "Child's inches is required";
    } else if (!gIsNumber(cInch)) {
      newErrors.cInch = "Please enter a valid number for inches";
    } else if (parseInt(cInch) < 0 || parseInt(cInch) > 11) {
      newErrors.cInch = "Inches should be between 0 and 11";
    }

    // Validate child weight
    if (!cWeight) {
      newErrors.cWeight = "Child weight is required";
    } else if (!gIsNumber(cWeight)) {
      newErrors.cWeight = "Please enter a valid weight";
    } else {
      const weightNum = parseFloat(cWeight);
      if (weightUnit === "pounds") {
        if (weightNum < 4 || weightNum > 1550) {
          newErrors.cWeight = "Weight should be between 4 and 1550 pounds";
        }
      } else {
        if (weightNum < 2 || weightNum > 700) {
          newErrors.cWeight = "Weight should be between 2 and 700 kgs";
        }
      }
    }

    return newErrors;
  }

  // Mid-parental height formula - EXACT FORMULA FROM THE IMAGE
  function calculateMidParentalHeight(motherInches, fatherInches, isMale) {
    // Using the exact formula from the image:
    // For Boys: (Father's Height + Mother's Height + 5) / 2
    // For Girls: (Father's Height + Mother's Height - 5) / 2
    if (isMale) {
      return (fatherInches + motherInches + 5) / 2;
    } else {
      return (fatherInches + motherInches - 5) / 2;
    }
  }

  // Handle radio button changes
  const handleRadioChange = (e) => {
    setFormData(prev => ({
      ...prev,
      gender: e.target.value
    }));
    
    if (errors.gender) {
      setErrors(prev => ({
        ...prev,
        gender: ""
      }));
    }
  };

  // Handle weight unit change
  const handleWeightUnitChange = (e) => {
    const newUnit = e.target.value;
    setFormData(prev => ({
      ...prev,
      weightUnit: newUnit,
      cWeight: "" // Clear weight when unit changes
    }));

    // Clear weight error when unit changes
    if (errors.cWeight) {
      setErrors(prev => ({
        ...prev,
        cWeight: ""
      }));
    }
  };

  // Calculate total age in years (with decimal for months)
  function calculateTotalAge(years, months) {
    return parseFloat(years) + (parseFloat(months) / 12);
  }

  // Main calculation function
  const ChildHeightPredictor = () => {
    const validationErrors = validateAllFields();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Mark all fields as touched to show errors
      const allTouched = {};
      Object.keys(validationErrors).forEach(key => {
        allTouched[key] = true;
      });
      setTouched(allTouched);
      return;
    }

    // Get form values
    const { gender, mFeet, mInch, fFeet, fInch } = formData;

    // Convert heights to inches
    const motherHeightInches = gToInches(mFeet, mInch);
    const fatherHeightInches = gToInches(fFeet, fInch);

    // Calculate predicted height using the EXACT mid-parental formula from the image
    const isMale = gender === "male";
    const predictedHeight = calculateMidParentalHeight(motherHeightInches, fatherHeightInches, isMale);

    // Display result
    setFormData(prev => ({
      ...prev,
      result: gToFeetAndInches(Math.round(predictedHeight))
    }));
  };

  // Clear form function
  const clearForm = () => {
    setFormData({
      gender: "female",
      ageYears: "",
      ageMonths: "",
      cFeet: "",
      cInch: "0",
      cWeight: "",
      weightUnit: "pounds",
      mFeet: "",
      mInch: "0",
      fFeet: "",
      fInch: "0",
      result: ""
    });
    setErrors({});
    setTouched({});
  };

  // Helper function to show error for a field
  const showError = (fieldName) => {
    return errors[fieldName] && touched[fieldName] ? (
      <div className="text-danger small mt-1" style={{lineHeight:"18px",fontSize:"11px"}}>{errors[fieldName]}</div>
    ) : null;
  };

  // Generate year options (4-17)
  const yearOptions = [];
  for (let i = 4; i <= 17; i++) {
    yearOptions.push(<option key={i} value={i}>{i}</option>);
  }

  // Generate month options (0-11)
  const monthOptions = [];
  for (let i = 0; i <= 11; i++) {
    monthOptions.push(<option key={i} value={i}>{i}</option>);
  }

  return (
    <>
      <div id="children_height_tab">
        <div className="row mt-2">
          <div className="col-md-6">
            <h3>Height Calculator – Children's Adult Height Predictor</h3>
            <p style={{ fontSize: 16, textAlign: "justify" }}>
              Curious how tall your child might be as an adult?
              This Children's Adult Height Predictor uses the Mid-Parental Height Formula 
              to estimate your child's future adult height based on parental and child data.
              Simply select your child's gender (boy or girl), enter their current age, height, 
              and weight, along with both parents' heights, then click Calculate to see the 
              predicted adult height.
            </p>
            
            <div className="clearfix"><br /></div>
            
            <h3>How It Works</h3>
            <p style={{ fontSize: 16, textAlign: "justify" }}>
              A child's adult height is mostly influenced by genetics, though factors such as 
              nutrition, sleep, exercise, and overall health also play a key role. While no 
              method can predict height with perfect accuracy, the Mid-Parental Formula provides 
              a reliable estimate widely used by pediatricians.
            </p>
            
            <div className="clearfix"><br /></div>

            {/* <h4>Formula</h4>
            <div style={{ fontSize: 14, textAlign: "justify", backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "5px" }}>
              <h5>Mid-Parental Height Formula</h5>
              
              <div style={{ marginBottom: "15px" }}>
                <strong>For Boys:</strong>
                <div style={{ textAlign: "center", margin: "10px 0" }}>
                  Predicted Height (Boy) = (Father's Height + Mother's Height + 5) ÷ 2
                </div>
                <p style={{ fontSize: "14px", margin: 0 }}>
                  <strong>Explanation:</strong> We add 5 inches to account for the average difference in adult height between males and females.
                </p>
              </div>

              <div>
                <strong>For Girls:</strong>
                <div style={{ textAlign: "center", margin: "10px 0" }}>
                  Predicted Height (Girl) = (Father's Height + Mother's Height - 5) ÷ 2
                </div>
                <p style={{ fontSize: "14px", margin: 0 }}>
                  <strong>Explanation:</strong> We subtract 5 inches since females are typically shorter than males on average.
                </p>
              </div>
            </div>

            <div className="clearfix"><br /></div> */}

            <p style={{ fontSize: 16, textAlign: "justify" }}>
              The result gives an estimated adult height. Most children reach an adult height 
              within ±4 inches (±10 cm) of this prediction. Girls typically reach their full 
              height earlier (around age 18), while boys continue growing until about age 21.
            </p>
          </div>

          <div className="col-md-6">
            <div style={{ width: "100%" }}>
              <div className="body-fat-ratio-heading">
                <h5>Children's Adult Height Predictor</h5>
              </div>

              <div style={{ backgroundColor: "#F7F7F7" }}>
                <div className="clearfix"><br /></div>
                
                <div
                  style={{
                    width: "100%",
                    padding: "0px 15px"
                  }}
                >
                  <div className="row" style={{ borderBottom: "0px" }}>
                    {/* Child's Gender */}
                    <div className="col-lg-12">
                      <label className="control-label mb-10">Child's Sex *</label>
                      <div className="input-group d-flex">
                        <div style={{ marginRight: 15 }}>
                          <input 
                            checked={formData.gender === "female"}
                            onChange={handleRadioChange}
                            value="female" 
                            name="gender" 
                            type="radio" 
                            id="gender_female"
                          />
                          <label htmlFor="gender_female" className="ms-2">Girl</label>
                        </div>
                        <div>
                          <input 
                            checked={formData.gender === "male"}
                            onChange={handleRadioChange}
                            value="male" 
                            name="gender" 
                            type="radio" 
                            id="gender_male" 
                          />
                          <label htmlFor="gender_male" className="ms-2">Boy</label>
                        </div>
                      </div>
                      {showError('gender')}
                    </div>

                    {/* Child Age */}
                    <div className="col-lg-12">
                      <label className="control-label mb-10">Child Age *</label>
                      <div className="row">
                        <div className="col-6">
                          <select 
                            name="ageYears" 
                            className="form-control w-100 mb-0"
                            value={formData.ageYears}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Years</option>
                            {yearOptions}
                          </select>
                          {showError('ageYears')}
                        </div>
                        <div className="col-6">
                          <select 
                            name="ageMonths" 
                            className="form-control mb-0"
                            value={formData.ageMonths}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Months</option>
                            {monthOptions}
                          </select>
                          {showError('ageMonths')}
                        </div>
                      </div>
                    </div>

                    {/* Child Height */}
                    <div className="col-lg-12 my-3">
                      <h5>Child Height *</h5>
                      <hr style={{
                        marginBottom: "-8px",
                        borderTop: "0.2px solid lightgray",
                      }} />
                    </div>
                    <div className="col-lg-6">
                      <label className="control-label mb-10">Feet</label>
                      <input
                        name="cFeet"
                        className="form-control mb-0"
                        placeholder="1-9"
                        type="number"
                        min="1"
                        max="9"
                        value={formData.cFeet}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                          // Prevent negative input
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {showError('cFeet')}
                    </div>
                    <div className="col-lg-6">
                      <label className="control-label mb-10">Inches *</label>
                      <input
                        name="cInch"
                        className="form-control mb-0"
                        placeholder="0-11"
                        type="number"
                        min="0"
                        max="11"
                        value={formData.cInch}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                          // Prevent negative input
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {showError('cInch')}
                    </div>

                    {/* Child Weight */}
                    <div className="col-lg-12 my-3">
                      <label className="control-label mb-10">Child Weight *</label>
                      <div className="d-flex justify-content-between align-items-center mb-0">
                        <div>
                          <input 
                            checked={formData.weightUnit === "pounds"}
                            onChange={handleWeightUnitChange}
                            value="pounds" 
                            name="weightUnit" 
                            type="radio" 
                            id="weight_pounds"
                            className="mb-0"
                          />
                          <label htmlFor="weight_pounds" className="ms-2 me-3">Pounds</label>
                          
                          <input 
                            checked={formData.weightUnit === "kgs"}
                            onChange={handleWeightUnitChange}
                            value="kgs" 
                            name="weightUnit" 
                            type="radio" 
                            id="weight_kgs"
                            className="mb-0" 
                          />
                          <label htmlFor="weight_kgs" className="ms-2">Kgs</label>
                        </div>
                        <label className="pull-right">
                          <strong>{formData.weightUnit === "pounds" ? "Pounds" : "Kgs"}</strong>
                        </label>
                      </div>
                      <input
                        name="cWeight"
                        className="form-control mb-0"
                        placeholder={formData.weightUnit === "pounds" ? "4-1550" : "2-700"}
                        type="number"
                        min={formData.weightUnit === "pounds" ? "4" : "2"}
                        max={formData.weightUnit === "pounds" ? "1550" : "700"}
                        value={formData.cWeight}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                          // Prevent negative input
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {showError('cWeight')}
                    </div>

                    {/* Mother Height */}
                    <div className="col-lg-12 my-3">
                      <h5>Mother Height *</h5>
                      <hr style={{
                        marginBottom: "-8px",
                        borderTop: "0.2px solid lightgray",
                      }} />
                    </div>
                    <div className="col-lg-6">
                      <label className="control-label mb-10">Feet</label>
                      <input
                        name="mFeet"
                        className="form-control mb-0"
                        placeholder="1-9"
                        type="number"
                        min="1"
                        max="9"
                        value={formData.mFeet}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                          // Prevent negative input
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {showError('mFeet')}
                    </div>
                    <div className="col-lg-6">
                      <label className="control-label mb-10">Inches *</label>
                      <input
                        name="mInch"
                        className="form-control mb-0"
                        placeholder="0-11"
                        type="number"
                        min="0"
                        max="11"
                        value={formData.mInch}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                          // Prevent negative input
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {showError('mInch')}
                    </div>

                    {/* Father Height */}
                    <div className="col-lg-12 my-3">
                      <h5>Father Height *</h5>
                      <hr style={{
                        marginBottom: "-8px",
                        borderTop: "0.2px solid lightgray",
                      }} />
                    </div>
                    <div className="col-lg-6">
                      <label className="control-label mb-10">Feet</label>
                      <input
                        name="fFeet"
                        className="form-control mb-0"
                        placeholder="1-9"
                        type="number"
                        min="1"
                        max="9"
                        value={formData.fFeet}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                          // Prevent negative input
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {showError('fFeet')}
                    </div>
                    <div className="col-lg-6">
                      <label className="control-label mb-10">Inches *</label>
                      <input
                        name="fInch"
                        className="form-control mb-0"
                        placeholder="0-11"
                        type="number"
                        min="0"
                        max="11"
                        value={formData.fInch}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                          // Prevent negative input
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      {showError('fInch')}
                    </div>

                    {/* Buttons */}
                    <div className="col-lg-12 my-4 d-flex justify-content-between">
                      <button
                        className="calculate"
                        onClick={ChildHeightPredictor}
                        name="Submit2"
                        type="button"
                        style={{ 
                          padding: "8px 20px",
                          width: "48%"
                        }}
                      >
                        Calculate
                      </button>
                      <button
                        name="reset"
                        onClick={clearForm}
                        value="Clear Values"
                        type="button"
                        className="value-clear-btn"
                        style={{ 
                          padding: "8px 20px",
                          width: "48%"
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Result Section */}
                  <p className="text-center alert alert-success">
                    <strong>Child's Predicted Height</strong>
                  </p>
                  <div className="col-lg-12 pb-3">
                    <label className="pull-right">
                      <strong>
                        {formData.gender === "male" ? "At 21 Years" : "At 18 Years"}
                      </strong>
                    </label>
                    <input
                      readOnly
                      name="result"
                      className="form-control"
                      placeholder="Result will appear here"
                      value={formData.result}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ChildrenHeightPredictor;